import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { Box, CircularProgress, debounce, InputAdornment, Paper, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DispoCalendarDateSelector from "./calendarElements/DispoCalendarDateSelector";
import KGPSelector from "./dialogs/KGPSelector";
import ApiService from "@/api/ApiService";
import { ResultOptions, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { CalJob, CalProject } from "./calendarElements/InnerCalendar";
import { TopBar } from "@/shared/structure/TopBar";
import KGPContainer from "./kgp/KGPContainer";
import { DndProvider } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { HTML5Backend } from "react-dnd-html5-backend";
import { CustomDragLayer } from "./kgp/CustomDragLayer";
import dayjs from "dayjs";
import OpenRequests from "./OpenRequests";
import useRights from "@/shared/api/useRights";
import KGPFreeProjectDrop from "./kgp/KGPFreeProjectDrop";
import { FetchingIndicator } from "./MainCalendar";
import { dateTimePickerTabsClasses } from "@mui/x-date-pickers";
import { Search } from "@mui/icons-material";
import JobInfoDialog from "./dialogs/JobInfoDialog";

interface Resource {
  id: number;
  name: string;
  number: string;
  lastLocation: any | null;
}

interface ResourceOrder {
  id: number;
  comment: string;
}

export interface ResourceOrderType extends ResourceOrder {
  key: number;
  type: "resourceOrder";
  wish: Resource | null;
  job: number;
  erfasser: Erfasser;
}

export interface ResourceBookingType {
  id: number;
  type: "kgBooking";
  resource: Resource;
}

export interface SpBookingType {
  id: number;
  type: "spBooking";
  resource: Resource;
}

export interface GhostType {
  id: number;
  type: "ghost";
  resource: Resource;
}

interface NoneType {
  id: 0;
  type: "none";
  name: string;
  content: GhostType[];
}

interface Erfasser {
  id: number;
  name: string;
}

interface SpecialPlaceType {
  id: number;
  type: "specialPlace";
  name: string;
  content: (GhostType | SpBookingType)[];
}

interface ProjectType {
  id: string;
  type: "project";
  hasPlanning: boolean;
  project: CalProject;
  erfasser: Erfasser;
  jobs: { id: number; phase: { id: number; name: string } }[];
  content: (ResourceOrderType | ResourceBookingType | GhostType)[];
}

export type KGPLocation = NoneType | SpecialPlaceType | ProjectType;

type KGPLocationType = KGPLocation["type"]; // Extract the literal union of types

const typeSorting: Record<KGPLocationType, number> = {
  none: 1,
  specialPlace: 2,
  project: 3,
};

const colors = [
  "#e57373",
  "#4db6ac",
  "#7986cb",
  "#f06292",
  "#81c784",
  "#64b5f6",
  "#ba68c8",
  "#aed581",
  "#4fc3f7",
  "#9575cd",
  "#dce775",
  "#4dd0e1",
  "#fff176",
  "#ffd54f",
  "#ffb74d",
  "#ff8a65",
];

export default function KGP() {
  const setPageTitle = useGlobalStore((state) => state.setPageTitle);
  const kgpReference = usePersistantStore((state) => state.kgpReference);
  const date = useGlobalStore((state) => state.date);
  const setDate = useGlobalStore((state) => state.setDate);
  const region = usePersistantStore((state) => state.region);
  const setKgpReference = usePersistantStore((state) => state.setKgpReference);
  useEffect(() => setPageTitle("Kleingeräteplanung"), []);
  const rights = useRights();

  const [searchString, setSearchString] = useState("");
  const [jobInfoId, setJobInfoId] = useState<number | null>(null);
  useEffect(() => setJobInfoId(null), [date, region, kgpReference]);
  const [searchInput, setSearchInput] = useState("");

  const handleSearchChange = useMemo(
    () =>
      debounce((val) => {
        setSearchString(val);
      }, 500),
    [],
  );
  const handleSearchInput = (e) => {
    const val = (e.target as HTMLInputElement).value;
    setSearchInput(val);
    handleSearchChange(val);
  };

  const query = useQuery<KGPLocation[], AxiosResponse>({
    queryKey: ["getKGP", "" + kgpReference, date, "" + region],
    queryFn: ApiService.getKGP(kgpReference, date, region),
  });

  const transferQuery = useQuery<any[], AxiosResponse>({
    queryKey: ["getTransfers", "" + (region || 0), date],
    queryFn: ApiService.getDailyTransfers(region || 0, date),
  });

  const transferredNumbers = useMemo(() => new Set((transferQuery.data ?? []).map((x) => x.resource_number)), [transferQuery.data]);

  const containers = useMemo(() => {
    if (!query.data) return [];
    const sorted = query.data?.sort((a, b) => {
      if (a.type !== b.type) return typeSorting[a.type] < typeSorting[b.type] ? -1 : 1;
      if (a.type === "project") return a.project.name.localeCompare(b.project.name) || a.erfasser.name.localeCompare(b.erfasser.name);
      if (a.type === "none" || a.type === "specialPlace") return a.name.localeCompare(b.name);
      return 0;
    });
    const c: JSX.Element[] = [];
    let currentColor = -1;

    for (let i = 0; i < sorted.length; i++) {
      const x = sorted[i];

      // gracefully handle missing project or number
      const now = x?.project?.number ?? "";

      // look at neighbors, safe fallback for edges
      const lastNumber = i > 0 ? (sorted[i - 1]?.project?.number ?? "") : "";
      const nextNumber = i < sorted.length - 1 ? (sorted[i + 1]?.project?.number ?? "") : "";

      let sameColor = "";

      if (now !== "") {
        if (now === lastNumber) {
          // same as previous → reuse current color
          sameColor = colors[Math.max(currentColor, 0) % colors.length];
        } else if (now === nextNumber) {
          // same as next → increment color index
          currentColor++;
          sameColor = colors[currentColor % colors.length];
        }
      }

      c.push(
        <KGPContainer
          transferredNumbers={transferredNumbers}
          sameColor={sameColor}
          searchString={searchString}
          disabled={!("DISPOSITION" in rights)}
          date={date}
          key={x.type + x.id}
          x={x}
          setJobInfoId={setJobInfoId}
        />,
      );
    }

    return c;
  }, [query.data, transferredNumbers, searchString, date, rights]);

  return (
    <DndProvider options={HTML5toTouch}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 0,
            background: "#eee",
          }}
        >
          <TopBar>
            <KGPSelector reference={kgpReference} onChange={setKgpReference} />
            <DispoCalendarDateSelector />
            <KGPFreeProjectDrop />
            <FetchingIndicator />
            <div style={{ flex: 1 }} />
            <TextField
              value={searchInput}
              onChange={handleSearchInput}
              size="small"
              placeholder="Geräte filtern..."
              sx={{ minWidth: 360, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </TopBar>
          {query.isLoading ? (
            <CircularProgress />
          ) : query.isLoadingError ? (
            "Error"
          ) : (
            <div className="kgpwrapper2">
              <div className="kgpwrapper">{containers}</div>
            </div>
          )}
        </Box>
        {!("DISPOSITION" in rights) ? null : <OpenRequests type="SHORT" currentDate={date} />}
      </Box>
      <CustomDragLayer />
      {jobInfoId !== null ? <JobInfoDialog jobId={jobInfoId} close={() => setJobInfoId(null)} /> : null}
    </DndProvider>
  );
}
